import type {
  RejectInvoiceMutationOptions,
  RejectInvoiceVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../queryKeys';

import { rejectInvoice } from './rejectInvoice';

/**
 * Mutation hook for rejecting an invoice.
 *
 * Invalidates detail, timeline, and V2 list queries before settlement.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns A mutation object to trigger the rejection.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/reject_invoice_invoices__invoice_id__reject_post
 */
export function useMutationRejectInvoice(
  options?: RejectInvoiceMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, payload }: RejectInvoiceVariables) =>
      rejectInvoice(invoiceId, payload),
    ...options,
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getDetail(variables.invoiceId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getTimeline(variables.invoiceId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getAllV2({}),
        }),
      ]);

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
}
